import {noop} from 'lodash';
import {
  getPageComponentRootElement,
  getRelativePath,
  resolveSlotDefaultComponentBoundary,
  visitNativeIdPathSymbolInstances,
} from '@packages/utilities/platform';
import {getOverridableDataPathsArray} from './OverridableDataPaths';
import {
  Component,
  Expressions,
  matchExpression,
  Plugins,
  type Type,
  Types,
} from '@packages/systems/wfdl';
import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getGuidWithFullNativeId,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';

/**
 * A `LinkedFieldsPath` represents the link between a field on a symbol instance to some target element.
 * It is an array of N ids, where the first N-1 ids are ids of fields through which the connection goes.
 * The last N-th id is the id of the target element. (Notice that this is somewhat similar to `NativeId` paths)
 *
 * Example of a linked fields path: ['field-1', 'field-2', 'element-id']
 * This path represents a sequence of fields that goes through 2 symbol instances to a nested target element with id `element-id`.
 * The outermost symbol instance has a field with id `field-1`.
 * A symbol instance within it has a field id `field-2` that is linked to `field-1`.
 * Finally, an element inside the inner symbol is bound to `field-2`.
 */
type LinkedFieldsPath = string[];

/**
 * A `LinkedFieldsPathMap` is a `fieldId -> LinkedFieldsPath` mapping where `fieldId` is the first field in a `LinkedFieldsPath`.
 * This mapping allows us to keep track of the source of a path as we build it.
 *
 * Example:
 *
 * {
 *   'field-1': ['field-1', 'field-2', 'element-id']
 * }
 */
type LinkedFieldsPathMap = {
  [fieldId: string]: LinkedFieldsPath;
};

/**
 * Given a `nativeIdPath` and a `recordFields` object, will create a `LinkedFieldsPathMap` that contains
 * the path from fields in `recordFields` to the target element provided by `nativeIdPath`.
 * If a path from a field id to the target element doesn't exist and is incomplete, the `LinkedFieldsPathMap`
 * will contain an empty array for that field id.
 *
 * Example:
 *
 * @input `nativeIdPath`: ['field-1', 'field-2', 'element-id']
 * @input `recordFields`: { 'field-1': {...}, 'field-2': {...} }
 *
 * {
 *   'field-1': ['field-1', 'field-2', 'element-id'],
 *   'field-2': []
 * }
 */
export const createLinkedFieldsPathMap = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  recordFields: {
    [key: string]: Type;
  },
  nativeIdPathBoundary: NativeId[],
  plugins?: Plugins.PluginsType
): LinkedFieldsPathMap => {
  const rootElem = getPageComponentRootElement(componentMap);
  if (!rootElem) return {};

  /**
   * Used to keep track of a given field key to the head (first item) of
   * the `LinkedFieldsPath`. This helps us later to quickly find which path needs to be updated
   * as we go through fields.
   */
  const fieldKeyToPathHead = new Map<string, string>();

  /**
   * Initialize the result map & `fieldKeyToPathHead` map
   */
  const resultMap: LinkedFieldsPathMap = Object.keys(recordFields).reduce<
    Record<string, any>
  >((res, fieldKey: string) => {
    fieldKeyToPathHead.set(fieldKey, fieldKey);
    res[fieldKey] = [fieldKey];
    return res;
  }, {});

  /**
   * Used to store the innermost root element that will be obtained
   * during the visit of symbol instances below. We will use it during the
   * second step of the algorithm.
   */
  let innermostRoot: ExpressionElement | null = null;

  /**
   * STEP 1/2
   * Visit the symbol instances of the `nativeIdPath` and find the field
   * connections that go through them
   */

  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    rootElem,
    componentMap,
    (symInstance, symRoot) => {
      innermostRoot = symRoot;

      const symData = Expressions.getElementData(symInstance);

      /**
       * For every field on a symbol instance, determine which parent field it connects to
       * and update the result map by retreiving the correct path for that parent, and appending
       * the child field to it.
       */
      matchFieldRecordExpression(symData, (childFieldKey, fieldExpr) => {
        const parentFieldKey = getParentFieldKey(fieldExpr);
        const pathHeadKey =
          parentFieldKey && fieldKeyToPathHead.get(parentFieldKey);

        if (pathHeadKey) {
          const curr = resultMap[pathHeadKey] || null;
          resultMap[pathHeadKey] = curr
            ? [...curr, childFieldKey]
            : [childFieldKey];
          fieldKeyToPathHead.set(childFieldKey, pathHeadKey);
        }
      });
    },
    plugins
  );

  /**
   * STEP 2/2
   * Visit the last item in `nativeIdPath` separately, which is the target element.
   */

  const {guid, fullNativeId} = getGuidWithFullNativeId(
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    nativeIdPath[nativeIdPath.length - 1]
  );

  const targetElement =
    innermostRoot && guid
      ? Expressions.getElementById(EElement(innermostRoot), guid)
      : null;

  if (targetElement && innermostRoot) {
    const targetElementData = Expressions.getElementData(targetElement);

    const fieldKeys = getOverridableDataPathsArray().map((dataPath) => {
      const targetElementPathValue = Expressions.getIn(
        targetElementData,
        dataPath.path
      );
      return targetElementPathValue
        ? getParentFieldKey(targetElementPathValue)
        : null;
    });

    /**
     * Below we check if the paths we've built so far have gone through all the instances as we expect.
     * If we did, we add the final target element id to the path. Otherwise we empty the path.
     * If the target element is the root of the innermost symbol, we will add that
     * root id to all paths.
     */
    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
    const isTargetElementSymbolRoot = targetElement.id === innermostRoot.id;

    return Object.entries(resultMap).reduce<Record<string, any>>(
      (res, [resultMapKey, path]: [any, any]) => {
        const pathTail = path[path.length - 1];
        const hasVisitedAllSymbols =
          path.length ===
          getRelativePath(nativeIdPath, nativeIdPathBoundary).length - 1;

        const isTargetElementReached =
          hasVisitedAllSymbols &&
          (isTargetElementSymbolRoot || fieldKeys.includes(pathTail));

        res[resultMapKey] = isTargetElementReached
          ? path.concat(fullNativeId)
          : [];
        return res;
      },
      {}
    );
  }

  /**
   * If we got here, we were unable to get to the target element
   * so we reset all the incomplete paths in `resultMap`
   */
  return emptyLinkedFieldsPathMap(resultMap);
};

/**
 * Given a symbol instance expression (of the outermost instance in a selection)
 * and a native id path to a selected element, returns linked fields between them
 */
export const getLinkedFieldsPathMapForElement = (
  symInst: ExpressionElement,
  selectedNativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  liveComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): LinkedFieldsPathMap | Record<any, any> => {
  const elementDataType =
    getDataTypeFromElement(plugins)(liveComponentMap)(symInst);
  const recordFields = Types.getRecordFields(elementDataType);
  let nativeIdPathBoundary = focusedSymbolNativeIdPath;

  if (focusedSymbolNativeIdPath.length === 0) {
    const componentBoundary = resolveSlotDefaultComponentBoundary(
      selectedNativeIdPath,
      liveComponentMap,
      plugins
    );
    if (componentBoundary) {
      nativeIdPathBoundary = componentBoundary.nativeIdPathBoundary;
    }
  }

  return recordFields
    ? createLinkedFieldsPathMap(
        selectedNativeIdPath,
        liveComponentMap,
        recordFields,
        nativeIdPathBoundary,
        plugins
      )
    : {};
};

/**
 * UTILITIES AND MATCHER FUNCTIONS IMPLEMENTED BELOW
 */

const constantNull = (_: Expression) => null;

const getParentFieldKey: (expr: Expression) => string | null = matchExpression({
  Select: (val) => {
    if (Expressions.isDataVariable(val.from)) return val.prop;
    return null;
  },
  Call: ({arg}) => getParentFieldKey(arg),
  _: constantNull,
});

type MatchFieldRecordExprFn = (
  expr: Expression,

  callbackFn: (key: string, expr: Expression) => void
) => void;

const fieldsRecordExpressionMatcher = matchExpression({
  Record: (fields) => (callbackFn: (key: string, expr: Expression) => void) => {
    Object.entries(fields).forEach(([fieldKey, fieldExpr]: [any, any]) => {
      callbackFn(fieldKey, fieldExpr);
    });
  },
  _: (_) => noop,
});

const matchFieldRecordExpression: MatchFieldRecordExprFn = (expr, callbackFn) =>
  fieldsRecordExpressionMatcher(expr)(callbackFn);

const emptyLinkedFieldsPathMap = (map: LinkedFieldsPathMap) => {
  return Object.keys(map).reduce<Record<string, any>>((res, currKey) => {
    res[currKey] = [];
    return res;
  }, {});
};
